import React from 'react';
import type { ICellRendererParams } from 'ag-grid-community';
import RecordLink from '../../links/RecordLink';
import type { ClientSideSelectTables } from '../../../types/grid';

/**
 * Cell renderer for client-side relationship fields in AG Grid.
 *
 * Displays the relationship label and a RecordLink icon for navigation.
 * The link enables right-click, middle-click, and Ctrl/Cmd-click to open in new tab.
 */

interface ClientSideRelationshipCellRendererParams extends ICellRendererParams {
  relationship?: {
    target_table_name: string;
    target_label_field: string;
  };
  clientSideSelectTables?: ClientSideSelectTables;
  /** Edit mode: drop the link so the whole cell opens the editor. */
  suppressLink?: boolean;
}

export const ClientSideRelationshipCellRenderer: React.FC<ClientSideRelationshipCellRendererParams> = (params) => {
  const { value, relationship, clientSideSelectTables, suppressLink } = params;

  if (!value || value === null || !relationship) {
    return <span></span>;
  }

  // Get label from flattened lookup
  const key = `${relationship.target_table_name}_${value}`;
  const displayValue = clientSideSelectTables?.flattened?.[key]?.label || 'Error';

  if (suppressLink) return <span>{displayValue}</span>;

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <RecordLink
        tableName={relationship.target_table_name}
        recordId={value}
        className="record-link-in-cell"
      />
      <span>{displayValue}</span>
    </div>
  );
};

/**
 * Cell renderer for server-side relationship fields in AG Grid.
 *
 * Displays the relationship label and a RecordLink icon for navigation.
 */

interface ServerSideRelationshipCellRendererParams extends ICellRendererParams {
  fieldName?: string;
  relationship?: {
    target_table_name: string;
    target_label_field: string;
  };
  /** Edit mode: drop the link so the whole cell opens the editor. */
  suppressLink?: boolean;
}

export const ServerSideRelationshipCellRenderer: React.FC<ServerSideRelationshipCellRendererParams> = (params) => {
  const { value, data, fieldName, relationship, suppressLink } = params;

  if (!value || value === null || !relationship || !fieldName) {
    return <span></span>;
  }

  // Get display value from the _rel field
  let displayValue = 'Error';
  if (value?.data?.label) {
    displayValue = value.data.label;
  } else if (data?.[`${fieldName}_rel`]?.label) {
    displayValue = data[`${fieldName}_rel`].label;
  }

  // Get the ID for the link
  const recordId = typeof value === 'object' && value.data?.id
    ? value.data.id
    : value;

  if (!recordId) {
    return <span>{displayValue}</span>;
  }

  if (suppressLink) return <span>{displayValue}</span>;

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <RecordLink
        tableName={relationship.target_table_name}
        recordId={recordId}
        className="record-link-in-cell"
      />
      <span>{displayValue}</span>
    </div>
  );
};
